/* Central error toast mapper.
 *
 * Dispatch 17 rewrite:
 *   * The axios interceptor in `/lib/api.js` now auto-invokes this
 *     module on every non-2xx response and stamps `err._surfaced =
 *     true`. Legacy call sites doing `catch (e) { errorToast(e) }` are
 *     still supported -- the second call sees the flag and returns
 *     silently.
 *   * A 2-second dedup window suppresses identical (status + text)
 *     messages so a burst of parallel failures (e.g. 3 concurrent 403s
 *     from a page-load fan-out) renders exactly one toast.
 *   * 422 responses carry a Pydantic validation array as `detail`.
 *     The first 2-3 field errors are joined into a readable line.
 *   * 403 backend copy of the form `Missing permission: X.Y` now maps
 *     to the friendly "You don't have permission..." string.
 *
 * Public API (unchanged):
 *   errorToast(err, {opId?, silent?})
 *   recordApiCall(entry)
 *   openBugReporter(err?)
 */
import React from "react";
import { toast } from "sonner";

// ============================================================
// In-memory API ring buffer (last 5 calls). Populated by the axios
// interceptor -- see `/lib/api.js`.
// ============================================================
const API_RING = [];
const RING_SIZE = 5;

export function recordApiCall(entry) {
  API_RING.push({
    ...entry,
    at: new Date().toISOString(),
  });
  while (API_RING.length > RING_SIZE) API_RING.shift();
}

function snapshotContext() {
  const nav = typeof window !== "undefined" ? window.location : {};
  const safeLocal = {};
  try {
    for (const k of Object.keys(localStorage)) {
      // Never leak tokens / secrets. Only allow known-safe keys.
      if (/token|secret|password|key|session|auth|jwt|bearer/i.test(k)) continue;
      const v = localStorage.getItem(k);
      if (v && v.length < 200) safeLocal[k] = v;
    }
  } catch {
    /* private mode / cross-origin -- ignore */
  }
  return {
    route: `${nav?.pathname || ""}${nav?.search || ""}`,
    href: nav?.href || "",
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    timestamp: new Date().toISOString(),
    last_api_calls: [...API_RING],
    local_storage_keys: safeLocal,
  };
}

// ============================================================
// Message mapping. Backend detail codes -> user copy.
// ============================================================
const CODE_MAP = [
  // Auth (401 from /auth/* is left as-is because the caller decides UX;
  // this row catches downstream 401 from any expired-session flow).
  { match: (s) => /Not authenticated|Invalid token|token.*expired|Session expired|Session revoked/i.test(s),
    friendly: "Your session has ended — please sign in again.", kind: "auth" },
  // Login form -- wrong password / unknown email. Pass server copy
  // through so users see the exact reason (already user-safe strings).
  { match: (s) => /Invalid email or password|Invalid credentials|Incorrect (email|password|username)|Wrong password|User not found|Account (locked|disabled|inactive)|Too many attempts|Password expired/i.test(s),
    friendly: null, kind: "auth_form" },
  // Permissions -- covers "Missing permission: X.Y", "Insufficient permissions",
  // "CEO / ADMIN only", "Forbidden", plus "X only." trailing constructions.
  { match: (s) => /Forbidden|Insufficient permissions|Missing permission|CEO.*only|ADMIN.*only|\bonly\.?$/i.test(s),
    friendly: "You don't have permission to perform this action.", kind: "permission" },
  // Validation -- pass server copy through so users see field names.
  { match: (s) => /required|must be|invalid.*format|validation|Field required/i.test(s),
    friendly: null, kind: "validation" },
  // Network / timeout
  { match: (s) => /Network Error|ECONNREFUSED|ETIMEDOUT|timeout/i.test(s),
    friendly: "Network hiccup. Check your connection and try again.", kind: "network" },
  // Not found -- pass server copy (usually names the entity).
  { match: (s) => /not found|does not exist/i.test(s),
    friendly: null, kind: "not_found" },
  // Duplicate / conflict -- pass server copy.
  { match: (s) => /already exists|duplicate|clash/i.test(s),
    friendly: null, kind: "conflict" },
];

// ============================================================
// 2-second dedup window keyed by (status + friendly-text). Prevents a
// page-load fan-out of parallel failures from stacking identical toasts.
// ============================================================
const DEDUP_WINDOW_MS = 2000;
const _dedupSeen = new Map();

function _dedupKey(text, status) { return `${status || 0}:${text}`; }

function _shouldSuppress(text, status) {
  const now = Date.now();
  const key = _dedupKey(text, status);
  const last = _dedupSeen.get(key) || 0;
  if (now - last < DEDUP_WINDOW_MS) return true;
  _dedupSeen.set(key, now);
  // Cap map size on long-running sessions.
  if (_dedupSeen.size > 64) {
    const oldest = [..._dedupSeen.entries()].sort((a, b) => a[1] - b[1])[0]?.[0];
    if (oldest) _dedupSeen.delete(oldest);
  }
  return false;
}

// Pydantic v2 detail array -> "field: msg · field: msg" (max 3).
function _extractPydantic(detailArr) {
  if (!Array.isArray(detailArr) || detailArr.length === 0) return null;
  const parts = detailArr.slice(0, 3).map((it) => {
    const locArr = Array.isArray(it?.loc) ? it.loc : [];
    // Skip the leading "body"/"query"/"path" prefix that Pydantic emits.
    const tail = locArr.filter((seg) => !["body", "query", "path"].includes(seg));
    const loc = tail.length ? tail.join(".") : (locArr[0] || "field");
    const msg = it?.msg || "invalid";
    return `${loc}: ${msg}`;
  });
  return parts.join(" · ");
}

function extractDetail(err) {
  if (!err) return { text: "Unknown error", status: 0 };
  if (typeof err === "string") return { text: err, status: 0 };
  const status = err?.response?.status || 0;
  const data = err?.response?.data;
  let raw = null;
  if (data) {
    if (typeof data === "string") raw = data;
    else if (typeof data === "object") {
      // 422 -- Pydantic validation array.
      if (Array.isArray(data.detail)) {
        raw = _extractPydantic(data.detail);
      } else {
        raw = data.detail || data.message || data.error;
      }
      if (raw && typeof raw === "object") {
        raw = Array.isArray(raw) ? _extractPydantic(raw) : JSON.stringify(raw);
      }
    }
  }
  if (!raw) raw = err.message || "Something went wrong";
  return { text: String(raw), status };
}

// ============================================================
// Bug reporter modal state (module-scoped; a mount consumer subscribes)
// ============================================================
const bugSubscribers = new Set();
let currentBug = null;

export function openBugReporter(err) {
  currentBug = { err, context: snapshotContext(), extracted: extractDetail(err) };
  bugSubscribers.forEach((cb) => cb(currentBug));
}

export function closeBugReporter() {
  currentBug = null;
  bugSubscribers.forEach((cb) => cb(null));
}

export function useBugReporter() {
  const [state, setState] = React.useState(currentBug);
  React.useEffect(() => {
    bugSubscribers.add(setState);
    return () => bugSubscribers.delete(setState);
  }, []);
  return state;
}

// ============================================================
// Public entry point.
// ============================================================
export function errorToast(err, { opId, silent } = {}) {
  if (silent) return { text: "", kind: "silent", status: 0 };
  // Legacy call site: interceptor already surfaced this error.
  if (err && typeof err === "object" && err._surfaced === true) {
    return { text: "", kind: "already-surfaced", status: err?.response?.status || 0 };
  }

  const { text, status } = extractDetail(err);
  // Match against known patterns.
  let friendly = null;
  let kind = "unknown";
  for (const m of CODE_MAP) {
    if (m.match(text)) {
      friendly = m.friendly;
      kind = m.kind;
      break;
    }
  }
  // Status-driven fallback: a bare 403 with an unrecognised message
  // should still map to the friendly permission copy rather than dump
  // the raw backend string.
  if (kind === "unknown" && status === 403) {
    friendly = "You don't have permission to perform this action.";
    kind = "permission";
  }

  const isUnknown = kind === "unknown";
  const finalText = friendly || text;

  // Stamp _surfaced regardless of whether we actually rendered a toast
  // -- callers should not double-surface even for deduped errors.
  if (err && typeof err === "object") err._surfaced = true;

  if (_shouldSuppress(finalText, status)) {
    return { text: finalText, kind, status, deduped: true };
  }

  // D48-d -- auth kind (expired / revoked session) is now a neutral
  // "info" toast, not a danger-red one. The redirect-to-login flow in
  // `api.js` handles the actual navigation; the toast is purely to
  // explain *why* the page just refreshed. Red framing caused two
  // "the app is broken!" false alarms -- see patterns.md "PWA update
  // prompt + softened auth toast".
  if (kind === "auth") {
    toast.info(SOFTENED_AUTH_COPY, {
      id: opId || "auth-expired",
      duration: 5000,
      description: "You'll be redirected to sign in.",
    });
    return { text: SOFTENED_AUTH_COPY, kind, status };
  }

  toast.error(finalText, {
    id: opId,
    duration: isUnknown ? 8000 : 5000,
    description: status && kind === "unknown" ? `HTTP ${status}` : undefined,
    action: isUnknown
      ? { label: "Report bug", onClick: () => openBugReporter(err) }
      : undefined,
  });
  return { text: finalText, kind, status };
}

// D48-d -- exported so unit tests can assert the softened copy is used.
export const SOFTENED_AUTH_COPY = "Your session has ended — please sign in again.";

// Convenience for imperative bug reports (e.g. UI-only errors, not HTTP)
export function reportManualBug(context) {
  currentBug = {
    err: null,
    context: { ...snapshotContext(), ...context },
    extracted: { text: "Manual bug report", kind: "manual" },
  };
  bugSubscribers.forEach((cb) => cb(currentBug));
}

// Test-only helper -- resets the dedup window so unit tests can fire
// the same message twice back-to-back.
export function __resetDedupForTests() { _dedupSeen.clear(); }

export default errorToast;
